import { WithOwnerId } from '@mews-commander/types';
import { ActionType } from '@mews-commander/enums';
import { lt, useUrlSyncedTableState } from '@mews/framework';
import { IconName } from '@optimus-web/icons';
import { Card, OptimusProvider, useEnterpriseBuildLocation } from '@mews-commander/ui';
import { useEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { EnterpriseRelationshipOverviewEntry } from '@mews-commander/data-access';
import { Table, TableProvider } from '@optimus-web/core';
import { BaseLayout } from '../../../components/layout';
import { useGetEnterpriseRelationshipsOverviewEntries } from '../queries';
import { HeadingAction } from '../../../legacy';
import { Breadcrumbs } from '../../../components';
import { useColumns } from './columns';

export const PortfoliosPropertiesOverviewTable = ({ ownerId }: WithOwnerId) => {
    const { name, chainId } = useEnterprise(ownerId);

    const buildLocation = useEnterpriseBuildLocation();

    const columns = useColumns(ownerId, name);
    const [tableState, setTableState] = useUrlSyncedTableState<EnterpriseRelationshipOverviewEntry>({
        columns,
        extraQuery: {
            OwnerId: ownerId,
        },
    });

    const { data, isFetching } = useGetEnterpriseRelationshipsOverviewEntries(tableState, ownerId);

    // TODO: once all the pages for this section are migrated from BE, useBreadcrumbs()
    const breadcrumbs: Breadcrumbs = [
        {
            text: lt('PortfolioPlural'),
            url: buildLocation({ to: 'Portfolio/Index' }).href,
        },
        {
            text: name,
            url: buildLocation({ to: 'Portfolio/Detail/$id', params: { id: ownerId } }).href,
        },
        {
            text: lt('Properties'),
            active: true,
        },
    ];

    const actions: HeadingAction[] = [
        {
            url: buildLocation({
                to: 'Enterprise/CreatePortfolio',
                search: { OwnerId: chainId, PortfolioId: ownerId },
            }).href,
            icon: IconName.Add,
            'data-test-icon-button': IconName.Add,
            text: lt('Property'),
            type: ActionType.Link,
        },
        {
            url: buildLocation({
                to: 'Portfolio/AddProperty',
                search: { OwnerId: ownerId },
            }).href,
            icon: IconName.PlusSimple,
            'data-test-icon-button': IconName.PlusSimple,
            text: lt('ExistingProperty'),
            type: ActionType.Link,
        },
    ];
    return (
        <OptimusProvider density="spacious">
            <BaseLayout breadcrumbs={breadcrumbs} actions={actions} padded>
                <Card>
                    <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                        <Table
                            uniqueRowId="id"
                            data={data?.data ?? []}
                            loading={isFetching}
                            amountOfRows={data?.totalRowCount ?? 0}
                        />
                    </TableProvider>
                </Card>
            </BaseLayout>
        </OptimusProvider>
    );
};
